const LOADED_MEASUREMENT_IDS = new Set();

function ensureDataLayer() {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
}

export function isGoogleAnalyticsConfigured(config) {
  return Boolean(config?.enabled && config?.measurementId);
}

export function loadGoogleAnalytics(measurementId) {
  if (!measurementId || typeof window === "undefined") {
    return;
  }

  if (LOADED_MEASUREMENT_IDS.has(measurementId)) {
    return;
  }

  ensureDataLayer();
  window.gtag("js", new Date());
  window.gtag("config", measurementId, { send_page_view: false });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  script.setAttribute("data-ga-measurement-id", measurementId);
  document.head.appendChild(script);

  LOADED_MEASUREMENT_IDS.add(measurementId);
}

export function trackGoogleAnalyticsPageView(measurementId, pagePath) {
  if (!measurementId || !pagePath) {
    return;
  }
  ensureDataLayer();
  window.gtag("event", "page_view", {
    page_path: pagePath,
    send_to: measurementId,
  });
}

const EVENT_MAP = {
  page_view: (metadata, extra) => ({
    name: "page_view",
    params: { page_path: extra.pagePath || metadata.path || metadata.pagePath },
  }),
  search_performed: (metadata) => ({
    name: "search",
    params: {
      search_term: metadata.query || metadata.searchTerm || "",
      results_count: metadata.total ?? metadata.resultsCount,
    },
  }),
  listing_viewed: (metadata, extra) => ({
    name: "view_item",
    params: {
      items: [{ item_id: extra.listingId || metadata.listingId }],
    },
  }),
  listing_saved: (metadata, extra) => ({
    name: "add_to_wishlist",
    params: { items: [{ item_id: extra.listingId || metadata.listingId }] },
  }),
  listing_unsaved: (metadata, extra) => ({
    name: "remove_from_wishlist",
    params: { items: [{ item_id: extra.listingId || metadata.listingId }] },
  }),
  lead_form_started: (metadata) => ({
    name: "begin_checkout",
    params: { lead_type: metadata.leadType },
  }),
  lead_submitted: (metadata) => ({
    name: "generate_lead",
    params: { lead_type: metadata.leadType },
  }),
  lead_form_submitted: (metadata) => ({
    name: "generate_lead",
    params: { lead_type: metadata.leadType },
  }),
  contact_submitted: (metadata) => ({
    name: "generate_lead",
    params: { lead_type: metadata.leadType || "contact" },
  }),
  cta_clicked: (metadata) => ({
    name: "select_promotion",
    params: { promotion_name: metadata.ctaId || metadata.label },
  }),
  phone_clicked: () => ({ name: "click", params: { link_type: "phone" } }),
  whatsapp_clicked: () => ({ name: "click", params: { link_type: "whatsapp" } }),
  email_clicked: () => ({ name: "click", params: { link_type: "email" } }),
  map_layer_toggled: (metadata) => ({
    name: "select_content",
    params: { content_type: "map_layer", item_id: metadata.layerId },
  }),
  campaign_impression: (metadata) => ({
    name: "view_promotion",
    params: { promotion_id: metadata.campaignId },
  }),
  campaign_click: (metadata) => ({
    name: "select_promotion",
    params: { promotion_id: metadata.campaignId },
  }),
  community_viewed: (metadata) => ({
    name: "view_item_list",
    params: { item_list_name: "community", item_id: metadata.communityId },
  }),
  broker_profile_viewed: (metadata) => ({
    name: "view_item",
    params: { items: [{ item_id: metadata.brokerId, item_category: "broker" }] },
  }),
  vendor_profile_viewed: (metadata) => ({
    name: "view_item",
    params: { items: [{ item_id: metadata.vendorId, item_category: "vendor" }] },
  }),
  experiment_assigned: (metadata) => ({
    name: "experiment_assigned",
    params: {
      experiment_key: metadata.experimentKey,
      variant_key: metadata.variantKey,
    },
  }),
};

export function trackGoogleAnalyticsEvent(eventType, metadata = {}, extra = {}) {
  if (!eventType || typeof window === "undefined") {
    return;
  }

  ensureDataLayer();

  const mapper = EVENT_MAP[eventType];
  const mapped = mapper
    ? mapper(metadata, extra)
    : {
        name: eventType,
        params: {
          ...metadata,
          page_path: extra.pagePath || metadata.pagePath,
          listing_id: extra.listingId || metadata.listingId,
        },
      };

  if (!mapped?.name) {
    return;
  }

  window.gtag("event", mapped.name, mapped.params || {});
}
